/**
 * Plan queries and mutations for TanStack Query (Vendor App)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPost, queryPut } from '../utils/request'

// Type definitions
export interface Plan {
  id?: number
  name: string
  description?: string
  modules?: Array<{
    id: number
    name: string
    description?: string
  }>
  module_ids?: number[]
  is_active?: boolean
  created_at?: string
  updated_at?: string
}

export interface PlanListParams {
  page?: number
  per_page?: number
  [key: string]: any
}

export interface PlanListResponse {
  items: Plan[]
  total?: number
  page?: number
  per_page?: number
}

// Cache configuration
const CACHE_TIMES = {
  PLANS: 5 * 60 * 1000, // 5 minutes
  PLAN_DETAIL: 2 * 60 * 1000, // 2 minutes
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch a list of plans
 */
export const usePlansQuery = (params: MaybeRefOrGetter<PlanListParams> = {}) => {
  return useQuery({
    queryKey: computed(() => queryKeys.planList(toValue(params))),
    queryFn: async (): Promise<PlanListResponse> => {
      const queryParams = toValue(params)
      return queryGet<PlanListResponse>('/v1/plans/', { params: queryParams })
    },
    staleTime: CACHE_TIMES.PLANS,
  })
}

/**
 * Query hook to fetch a single plan by ID
 */
export const usePlanQuery = (planId: MaybeRefOrGetter<number | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(planId)
      return id ? queryKeys.plan(id) : ['plans', 'null']
    }),
    queryFn: async (): Promise<Plan> => {
      const id = toValue(planId)
      if (!id) throw new Error('Plan ID is required')
      return queryGet<Plan>(`/v1/plans/${id}`)
    },
    staleTime: CACHE_TIMES.PLAN_DETAIL,
    enabled: computed(() => !!toValue(planId)),
  })
}

/**
 * MUTATIONS
 */

/**
 * Mutation hook to create a new plan
 */
export const useCreatePlanMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (plan: Plan): Promise<Plan> => {
      return queryPost<Plan>('/v1/plans/', plan)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.plans() })
    },
  })
}

/**
 * Mutation hook to update an existing plan
 */
export const useUpdatePlanMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ planId, data }: { planId: number; data: Partial<Plan> }): Promise<Plan> => {
      return queryPut<Plan>(`/v1/plans/${planId}`, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.plans() })
      queryClient.invalidateQueries({ queryKey: queryKeys.plan(variables.planId) })
    },
  })
}
